/**
 * Rechenkern für eine Seite: Viereck gerade ziehen, Filter anwenden, drehen
 * und GENAU EINMAL als JPEG kodieren. Wird vom Worker (render.worker.ts) und
 * vom Haupt-Thread-Rückfall (render.ts) gleichermaßen benutzt.
 */
import { aufbereiten, entzerren, zielGroesse, type Ecken, type Filter } from "./bild";

export type Drehung = 0 | 90 | 180 | 270;
export type RenderAuftrag = {
  quelle: Blob;
  ecken: Ecken;
  drehung: Drehung;
  filter: Filter;
};
export type RenderErgebnis = { blob: Blob; w: number; h: number };

/** Obergrenze je Seite. A4 aus einem 4K-Bild liegt knapp darunter; Bons nie. */
export const MAX_PIXEL = 6_000_000;
/** Einmal sauber kodieren — danach fasst niemand mehr die Pixel an. */
export const JPEG_QUALITAET = 0.88;

type Leinwand = OffscreenCanvas | HTMLCanvasElement;
type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export async function rendereKern(
  a: RenderAuftrag,
  neu: (w: number, h: number) => Leinwand,
  kodiere: (c: Leinwand, q: number) => Promise<Blob>,
): Promise<RenderErgebnis> {
  const bmp = await createImageBitmap(a.quelle);
  const c = neu(bmp.width, bmp.height);
  const ctx = c.getContext("2d", { willReadFrequently: true }) as Ctx2D;
  ctx.drawImage(bmp, 0, 0);
  bmp.close?.();
  const src = ctx.getImageData(0, 0, c.width, c.height);
  c.width = c.height = 1; // Speicher früh freigeben (iPhone)

  const z = zielGroesse(a.ecken);
  const s = Math.min(1, Math.sqrt(MAX_PIXEL / (z.w * z.h)));
  const r = entzerren(
    { data: src.data, width: src.width, height: src.height },
    a.ecken,
    z.w * s,
    z.h * s,
  );
  aufbereiten(r, a.filter);

  const flach = neu(r.width, r.height);
  (flach.getContext("2d") as Ctx2D).putImageData(
    new ImageData(r.data as unknown as Uint8ClampedArray<ArrayBuffer>, r.width, r.height),
    0,
    0,
  );
  let ziel: Leinwand = flach;
  if (a.drehung) {
    const quer = a.drehung % 180 !== 0;
    const d = neu(quer ? r.height : r.width, quer ? r.width : r.height);
    const dc = d.getContext("2d") as Ctx2D;
    dc.translate(d.width / 2, d.height / 2);
    dc.rotate((a.drehung * Math.PI) / 180);
    dc.drawImage(flach as CanvasImageSource, -r.width / 2, -r.height / 2);
    ziel = d;
  }
  const blob = await kodiere(ziel, JPEG_QUALITAET);
  return { blob, w: ziel.width, h: ziel.height };
}

